"use client";

import React from "react";
import { cn } from "utils";
import { AppRatingContentBody } from "./AppRatingContentBody";
import { AppSuggestionContentBody } from "./AppSuggestionContentBody";
import Translations from "@translate/components/Home/AppRating/AppRatingSuggestionContent";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

export type AppRatingSuggestionContentMode = "rating" | "suggestion";

export interface AppRatingSuggestionContentProps {
  mode: AppRatingSuggestionContentMode;
  onRateUs: () => void;
  onOfferSuggestion: () => void;
  onSubmit: () => void;
  onMaybeLater: () => void;
  className?: string;
  text: string;
  setText: (text: string) => void;
}

/** 主按钮/次按钮文案与回调（按 mode 区分） */
const getButtonConfig = (
  mode: AppRatingSuggestionContentMode,
  handlers: {
    onRateUs: () => void;
    onOfferSuggestion: () => void;
    onSubmit: () => void;
    onMaybeLater: () => void;
  },
) =>
  mode === "rating"
    ? {
        primary: {
          text: I18n.t("Rate Us on App Store"),
          onClick: handlers.onRateUs,
        },
        secondary: {
          text: I18n.t("Offer a Suggestion"),
          onClick: handlers.onOfferSuggestion,
        },
      }
    : {
        primary: { text: I18n.t("Send Feedback"), onClick: handlers.onSubmit },
        secondary: {
          text: I18n.t("Maybe Later"),
          onClick: handlers.onMaybeLater,
        },
      };

/**
 * App 内评分/建议弹窗 - 内容层
 * 按 mode 渲染评分态或建议态，按钮复用、文案由 mode 决定；关闭由 EdgenDrawer 提供。
 */
export const AppRatingSuggestionContent: React.FC<
  AppRatingSuggestionContentProps
> = ({
  mode,
  onRateUs,
  onOfferSuggestion,
  onSubmit,
  onMaybeLater,
  className,
  text,
  setText,
}) => {
  const isRating = mode === "rating";
  const { primary, secondary } = getButtonConfig(mode, {
    onRateUs,
    onOfferSuggestion,
    onSubmit,
    onMaybeLater,
  });

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-11",
        { "gap-6": !isRating },
        className,
      )}
    >
      {isRating ? (
        <AppRatingContentBody />
      ) : (
        <AppSuggestionContentBody text={text} setText={setText} />
      )}
      <div className="flex w-full flex-col gap-2">
        <button
          title={primary.text}
          aria-label={primary.text}
          data-openreplay-label="home_appratingsuggestioncontent_click"
          id="btn-app-rating-suggestion-content"
          type="button"
          className="button sm_button primary_button button_m flex h-11 w-full items-center justify-center rounded-2 text-content_inverse_primary"
          onClick={primary.onClick}
        >
          {primary.text}
        </button>
        <button
          title={secondary.text}
          aria-label={secondary.text}
          data-openreplay-label="home_appratingsuggestioncontent_click"
          id="btn-app-rating-suggestion-content-2"
          type="button"
          className="button sm_button button_m flex h-11 w-full items-center justify-center rounded-2 bg-transparent text-content_primary"
          onClick={secondary.onClick}
        >
          {secondary.text}
        </button>
      </div>
    </div>
  );
};
